import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, Moon, Sun, Bookmark, Star, Flag } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

export default function SignInModal({ isDarkMode = false, onToggleDarkMode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = useMemo(() => {
    try {
      const sp = new URLSearchParams(location.search);
      const val = sp.get('return');
      return val ? decodeURIComponent(val) : '/';
    } catch {
      return '/';
    }
  }, [location.search]);
  const [mode, setMode] = useState('signIn'); // 'signIn' | 'signUp'
  const [forgot, setForgot] = useState(false); // password reset view
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        navigate(returnTo || '/', { replace: true });
      }
    });
    return () => {
      sub.subscription?.unsubscribe?.();
    };
  }, [navigate, returnTo]);

  // Clear auth tokens from URL hash after Supabase reads them
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash || '';
    if (hash && (hash.includes('access_token') || hash.includes('refresh_token') || hash.includes('type='))) {
      // Ensure session has been parsed, then clear hash
      supabase.auth.getSession().finally(() => {
        const url = new URL(window.location.href);
        url.hash = '';
        window.history.replaceState({}, document.title, url.pathname + url.search);
      });
    }
  }, []);

  const ensureProfile = async (user) => {
    try {
      if (!user?.id) return;
      const displayName =
        user.user_metadata?.full_name ||
        username ||
        (user.email ? user.email.split('@')[0] : null);
      const payload = {
        user_id: user.id,
        ...(displayName ? { username: displayName } : {}),
        ...(user.email ? { email: user.email } : {})
      };
      await supabase.from('profiles').upsert(payload, { onConflict: 'user_id' });
    } catch (e) {
      // ignore profile upsert errors here; user auth is primary
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setBusy(true);
    try {
      if (forgot) {
        await handleSendResetEmail();
        return;
      } else if (mode === 'signIn') {
        const { error: signInError, data } = await supabase.auth.signInWithPassword({
          email,
          password
        });
        if (signInError) throw signInError;
        await ensureProfile(data?.user);
        navigate(returnTo || '/', { replace: true });
      } else {
        const { error: signUpError, data } = await supabase.auth.signUp(
          {
            email,
            password,
            options: { data: { full_name: username || undefined } }
          }
        );
        if (signUpError) throw signUpError;
        if (data?.user) await ensureProfile(data.user);
        setMessage('Check your email to confirm your account.');
      }
    } catch (err) {
      setError(err?.message || 'Authentication failed');
    } finally {
      setBusy(false);
    }
  };

  const handleSendResetEmail = async () => {
    try {
      const targetEmail = email || prompt('Enter your account email for password reset:') || '';
      if (!targetEmail) return;
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(targetEmail)) {
        alert('Please enter a valid email address.');
        return;
      }
      setBusy(true);
      const redirectTo = `${window.location.origin}/reset-password`;
      const { error: rpErr } = await supabase.auth.resetPasswordForEmail(targetEmail, { redirectTo });
      if (rpErr) throw rpErr;
      setMessage('Password reset email sent. Check your inbox.');
    } catch (e) {
      setError(e?.message || 'Failed to send reset email.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`fixed inset-0 z-50 ${isDarkMode ? 'dark' : ''}`}>
      <div
        className={`absolute inset-0 backdrop-blur-[2px] ${
          isDarkMode ? 'bg-[#111826]' : 'bg-white/80'
        }`}
        onClick={() => navigate(returnTo || '/', { replace: true })}
      />
      {/* Global dark mode toggle at top-right */}
      <button
        onClick={() => onToggleDarkMode && onToggleDarkMode()}
        className={`fixed top-4 right-4 z-20 p-2.5 rounded-lg transition-all duration-200 ${
          isDarkMode
            ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
        aria-label="Toggle dark mode"
      >
        {isDarkMode ? <Sun className="w-6 h-6" /> : <Moon className="w-6 h-6" />}
      </button>
      <div className="relative flex items-center justify-center min-h-screen px-4">
          {/* Logo placed above the modal card */}
        <div className="absolute top-8 left-1/2 transform -translate-x-1/2 z-10">
          <span className="font-bold text-2xl md:text-3xl select-none" style={{ fontFamily: 'Inter, sans-serif' }}>
            <span className="text-[#3b82f6]">Civic</span>
            <span className="text-[#ef4444]">Duty</span>
          </span>
        </div>
        <div className={`w-full ${mode === 'signUp' && !forgot ? 'max-w-4xl' : 'max-w-md'} flex ${mode === 'signUp' && !forgot ? 'flex-col md:flex-row' : ''} ${mode === 'signUp' && !forgot ? 'gap-6' : ''} items-start mt-20`}>
          {/* Benefits Modal - Only shown in signUp mode, now on the left */}
          {mode === 'signUp' && !forgot && (
            <div className="w-full md:flex-1 md:max-w-md order-1 md:order-1">
              <div className={`rounded-2xl shadow-2xl border ${isDarkMode ? 'bg-[#1f2a37] border-[#1f2a37]' : 'bg-[#f9fafb] border-gray-200'} h-full`}>
                <div className="p-6">
                  <h3 className={`text-xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Why Create an Account?
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                        <Bookmark className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          Save Bills
                        </h4>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Bookmark bills you want to track and access them anytime
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-yellow-900/30' : 'bg-yellow-50'}`}>
                        <Star className={`w-5 h-5 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          Get Notifications
                        </h4>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Receive email notifications when bills you're tracking change status
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-red-900/30' : 'bg-red-50'}`}>
                        <Flag className={`w-5 h-5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          Flag Inaccuracies
                        </h4>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Help improve CivicDuty by reporting site inaccuracies
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
          </div>
          )}
          
          {/* Main Auth Modal - Now on the right in signUp mode */}
          <div className={`w-full ${mode === 'signUp' && !forgot ? 'md:flex-1' : ''} ${mode === 'signUp' && !forgot ? 'md:max-w-md order-2 md:order-2' : ''}`}>
          <div className={`rounded-2xl shadow-2xl border ${isDarkMode ? 'bg-[#1f2a37] border-[#1f2a37]' : 'bg-[#f9fafb] border-gray-200'}`}>
          <div className="flex items-center justify-between p-4">
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {forgot ? 'Reset Password' : mode === 'signIn' ? 'Sign In' : 'Create an Account'}
            </h2>
            <button
              onClick={() => navigate(-1)}
              className={`p-2 rounded-lg ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="px-4 pb-5">
            <div className="space-y-3">
              {!forgot && mode === 'signUp' && (
                <input
                  type="text"
                  required
                  placeholder="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg text-sm border focus:outline-none ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'}`}
                />
              )}
              <input
                type="email"
                required
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg text-sm border focus:outline-none ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'}`}
              />
              {!forgot && (
                <input
                  type="password"
                  required
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg text-sm border focus:outline-none ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'}`}
                />
              )}
              <button
                type="submit"
                disabled={busy}
                className="w-full px-3 py-2.5 rounded-lg text-sm font-semibold bg-gradient-to-r from-red-600 to-blue-600 text-white hover:from-red-700 hover:to-blue-700 transition-colors"
              >
                {busy ? 'Please wait…' : forgot ? 'Send Reset Link' : mode === 'signIn' ? 'Sign In' : 'Create Account'}
              </button>
              {!forgot && mode === 'signIn' && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setForgot(true)}
                    disabled={busy}
                    className={`${isDarkMode ? 'text-blue-300' : 'text-blue-600'} text-xs underline mt-2`}
                  >
                    Forgot password?
                  </button>
                </div>
              )}
              {forgot && (
                <div className="flex justify-between items-center pt-1">
                  <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} text-xs`}>
                    Enter your account email to receive a reset link.
                  </span>
                  <button
                    type="button"
                    onClick={() => setForgot(false)}
                    className={`${isDarkMode ? 'text-blue-300' : 'text-blue-600'} text-xs underline`}
                  >
                    Back to Sign In
                  </button>
                </div>
              )}
              {/* Bottom CTA area */}
              {mode === 'signIn' ? (
                <div className="pt-4">
                  <div className={isDarkMode ? 'border-t border-gray-700 pt-4' : 'border-t border-gray-200 pt-4'} />
                  <button
                    type="button"
                    onClick={() => {
                      setMode('signUp');
                      setForgot(false);
                    }}
                    className="w-full px-3 py-2.5 rounded-lg text-sm font-semibold bg-green-600 text-white hover:bg-green-700 transition-colors"
                  >
                    Create an Account
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between text-sm">
                  <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                    Already have an account?
                  </span>
                  <button
                    type="button"
                    onClick={() => setMode('signIn')}
                    className={`${isDarkMode ? 'text-blue-400' : 'text-blue-600'} underline`}
                  >
                    Sign In
                  </button>
                </div>
              )}
              {message && <div className="text-xs text-green-500">{message}</div>}
              {error && <div className="text-xs text-red-500">{error}</div>}
            </div>
          </form>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}


